import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { TwitterIcon as Twitter, LinkedinIcon as Linkedin, GithubIcon as Github, InstagramIcon as Instagram, ArrowUpRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const footerLinks = {
  Services: [
    { id: 'fl-web-design', label: 'Web Design', href: '/services/web-design' },
    { id: 'fl-web-dev', label: 'Web Development', href: '/services/web-development' },
    { id: 'fl-ecommerce', label: 'E-Commerce', href: '/services/e-commerce' },
    { id: 'fl-seo', label: 'SEO & Performance', href: '/services/seo-performance' },
    { id: 'fl-uiux', label: 'UI/UX Design', href: '/services/ui-ux-design' },
    { id: 'fl-mobile', label: 'Mobile Apps', href: '/services/mobile-apps' },
  ],
  Industries: [
    { id: 'fl-hotels', label: 'Hotels & Hospitality', href: '#industries' },
    { id: 'fl-ecom-ind', label: 'E-Commerce & Retail', href: '#industries' },
    { id: 'fl-education', label: 'Education', href: '#industries' },
    { id: 'fl-healthcare', label: 'Healthcare', href: '#industries' },
    { id: 'fl-realestate', label: 'Real Estate', href: '#industries' },
    { id: 'fl-restaurants', label: 'Restaurants', href: '#industries' },
  ],
  Company: [
    { id: 'fl-about', label: 'About Us', href: '#about' },
    { id: 'fl-portfolio', label: 'Portfolio', href: '/projects' },
    { id: 'fl-process', label: 'Our Process', href: '#process' },
    { id: 'fl-blog', label: 'Blog', href: '/blog' },
    { id: 'fl-careers', label: 'Careers', href: '#' },
    { id: 'fl-contact', label: 'Contact', href: '#contact' },
  ],
  Legal: [
    { id: 'fl-privacy', label: 'Privacy Policy', href: '#' },
    { id: 'fl-terms', label: 'Terms of Service', href: '#' },
    { id: 'fl-cookies', label: 'Cookie Policy', href: '#' },
  ],
};

const socials = [
  { id: 'social-twitter', icon: Twitter, href: '#', label: 'Twitter' },
  { id: 'social-linkedin', icon: Linkedin, href: '#', label: 'LinkedIn' },
  { id: 'social-github', icon: Github, href: '#', label: 'GitHub' },
  { id: 'social-instagram', icon: Instagram, href: '#', label: 'Instagram' },
];

export default function PublicFooter() {
  return (
    <footer className="border-t border-border/40 bg-muted/20 pt-16 pb-8">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        {/* Top section */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-10 mb-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link href="/" className="flex items-center gap-2.5 mb-4">
              <AppLogo size={32} />
              <span className="font-display font-bold text-lg text-foreground">WebMint</span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-xs">
              Premium digital agency crafting high-performance websites and digital products for ambitious businesses worldwide.
            </p>
            <div className="space-y-1.5 mb-6">
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="text-primary">📍</span> Ward No 18, Chainpura, Piprali, Sikar
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="text-primary">📞</span> +91 90016 12303
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="text-primary">✉️</span> yogeshmahala77429@gmail.com
              </p>
            </div>
            <div className="flex items-center gap-2">
              {socials?.map((s) => {
                const Icon = s?.icon;
                return (
                  <a
                    key={s?.id}
                    href={s?.href}
                    aria-label={s?.label}
                    className="w-9 h-9 glass-card rounded-xl border border-border/40 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all duration-200"
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links columns */}
          {Object.entries(footerLinks)?.map(([category, links]) => (
            <div key={`footer-cat-${category}`} className="lg:col-span-1">
              <h4 className="font-mono-code text-[10px] font-medium tracking-widest text-muted-foreground/50 uppercase mb-4">{category}</h4>
              <ul className="space-y-2.5">
                {links?.map((link) => (
                  <li key={link?.id}>
                    <Link
                      href={link?.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 flex items-center gap-1 group"
                    >
                      {link?.label}
                      <ArrowUpRight size={10} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/30 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground/60 font-mono-code">
            © 2026 WebMint Agency. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
              All systems operational
            </span>
            <span className="text-xs text-muted-foreground/40 font-mono-code">v4.2.1</span>
          </div>
        </div>
      </div>
    </footer>
  );
}